"use client";

import { Suspense, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { usePathname } from "next/navigation";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SidebarBody } from "@/components/SiteSidebar";

/**
 * STORY-2007 follow-up (mobile-ux-baseline finding #6): the desktop
 * SiteSidebar hides at <768px, leaving Settings / Help / Builders /
 * PI / Admin / Categories unreachable from the chrome on phone. This
 * drawer carries the same nav + admin + category sections behind a
 * hamburger button in SiteHeader.
 *
 * Open/close lives in SiteHeader's local state. Drawer auto-closes on
 * pathname change (so tapping a nav link both navigates AND dismisses)
 * and on ESC. Body scroll is locked while open. Hidden at md:+ — the
 * desktop sidebar is already visible there.
 *
 * UX polish (PR follow-up): drawer is always mounted so open/close
 * can animate via a CSS transform + opacity transition (jarring
 * pop-in/pop-out otherwise). Focus moves to the close button on open
 * and restores to the originally-focused element on close. Tab-key
 * focus trap keeps keyboard navigation inside the dialog while open.
 */
export function MobileSidebarDrawer({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const pathname = usePathname();
  const dialogRef = useRef<HTMLElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  // Portal mount-gate: render null on SSR + first client paint so the
  // server-rendered tree matches the initial client tree. The portal
  // appears on the second render after useEffect fires, avoiding the
  // "client added <div className='fixed inset-0 ...'>" hydration
  // mismatch (Next 16 hydration warning + recoverable error).
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  // 2026-05-03 — `hasBeenOpened` lazy-mount removed. The flag was a
  // perf optimization that backfired: a user reported the drawer body
  // showing the MENU header but no nav links on /login. Root cause
  // is the prop→state→useEffect chain producing one frame where
  // `open=true` but `hasBeenOpened` was still false; with the inert+
  // pointer-events-none guards on the closed state the brief blank
  // frame got stuck visually on first open. Always-mount removes the
  // race; the duplicate api.me call is acceptable cost (desktop
  // SiteSidebar already issues one per page).

  // ESC handler. Only active while open.
  // 2026-05-24 — body scroll lock removed. It restored to `prev` on
  // cleanup, which could carry a stale "hidden" from another modal
  // and leave the page un-scrollable after the drawer closed.
  // Founder caught this 2026-05-24 ("the scrolling problem is back").
  // The drawer is `position: fixed; inset: 0` so it visually covers
  // the viewport anyway; the page underneath can scroll a tiny bit
  // but the user is looking at the drawer, not the page.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  // Focus management. Capture the previously-focused element on open
  // so we can return focus on close (a11y standard for modal dialogs);
  // move focus to the close button so keyboard users land inside the
  // dialog rather than at the page-level focus point.
  useEffect(() => {
    if (open) {
      previouslyFocusedRef.current =
        (document.activeElement as HTMLElement | null) ?? null;
      // Wait one frame so the close button is attached + the
      // animation has begun before we move focus to it.
      const id = requestAnimationFrame(() => {
        closeButtonRef.current?.focus();
      });
      return () => cancelAnimationFrame(id);
    }
    // Closing: restore focus to whatever had it before opening.
    previouslyFocusedRef.current?.focus();
    previouslyFocusedRef.current = null;
  }, [open]);

  // Tab focus trap. While the drawer is open, Tab / Shift+Tab cycles
  // focus among the dialog's own focusable descendants instead of
  // escaping to the (visually hidden) page underneath.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusable = dialog.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  // Auto-close on route change so tapping a nav link dismisses the
  // drawer without a second gesture.
  useEffect(() => {
    if (open) onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // SSR + first-paint safety — render null until mounted so the server
  // and client first-render trees match (no createPortal output on
  // either side until after hydration).
  if (!mounted) return null;

  // SiteHeader (the parent of this drawer's mount point) uses
  // backdrop-blur-md, which CSS-spec-mandates creates a containing
  // block for `position: fixed` descendants. Without a Portal the
  // drawer anchors to the SiteHeader's box (right-aligned off-canvas
  // on phones; same root cause as the PostComposer alignment bug).
  // createPortal escapes to <body> so `fixed inset-0` is once again
  // viewport-relative.
  return createPortal(
    <div
      className={`fixed inset-0 z-[100] md:hidden ${
        open ? "" : "pointer-events-none"
      }`}
      aria-hidden={open ? undefined : "true"}
      // STORY-2007 follow-up #2 — once the drawer has been opened
      // once, SidebarBody stays mounted to keep open animations
      // smooth + avoid re-firing api.me() on every nav. But its
      // links + buttons remain tabbable in the closed state via
      // standard keyboard navigation, even with pointer-events-none
      // (which only gates pointer input). React 19's `inert`
      // attribute pulls the whole subtree out of the focus order +
      // assistive-tech tree when closed; flipping it back on open
      // restores normal interaction. Without this, a keyboard user
      // cycling Tab on /home or any page would land inside the
      // hidden drawer mid-cycle.
      inert={!open}
    >
      <div
        aria-hidden="true"
        onClick={onClose}
        className={`absolute inset-0 bg-black/50 transition-opacity duration-200 ${
          open ? "opacity-100" : "opacity-0"
        }`}
      />
      <aside
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={t("drawer.aria")}
        className={`absolute left-0 top-0 flex h-full w-[280px] max-w-[85vw] flex-col overflow-y-auto border-r border-[var(--color-border)] bg-[var(--color-bg)] shadow-2xl transition-transform duration-200 ease-out ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
        style={{
          paddingTop: "env(safe-area-inset-top)",
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
      >
        <div className="flex items-center justify-between border-b border-[var(--color-border)] px-4 py-3">
          <span className="text-[11px] font-bold uppercase tracking-[0.6px] text-[var(--color-text3)]">
            {t("drawer.menuHeading")}
          </span>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            aria-label={t("drawer.closeAria")}
            className="rounded-md p-1.5 text-[var(--color-text2)] transition-colors hover:bg-[var(--color-surface)] hover:text-[var(--color-text)]"
          >
            <X size={16} strokeWidth={1.5} aria-hidden="true" />
          </button>
        </div>
        <div className="px-2 py-2">
          <Suspense fallback={null}>
            <SidebarBody />
          </Suspense>
        </div>
      </aside>
    </div>,
    document.body,
  );
}
